import { apiFetch } from '../client';
import { SharePointListClient, type ListItem } from '../SharePointListClient';
import { parseMoney } from '@/shared/lib/money';

/**
 * 18.Inventario — per-article stock buckets.
 *
 * **Internal-name quirks** (verified via `inspectList.ts Inventario`):
 *   - `StockIngresado_I` internal ↔ `StockDisponible_I` display
 *     — semantically the **Disponible** bucket (available for sale).
 *   - `StockReservado_I` internal ↔ `StockReservadoPendiente_I` display
 *     — the **Reservado Pendiente** bucket (accepted venta awaiting pago).
 *   - `StockPendiente_I` internal ↔ `StockCotizado_I` display
 *     — the **Cotizado** bucket (open cotización, pre-accept).
 *   - Every other Stock*_I column has matching internal/display names.
 *
 * Writers (server routes) always use the INTERNAL names. Consumers on
 * the client treat Disponible as synonymous with `StockIngresado_I`.
 */
export type InventarioFields = {
  Title?: string | null;
  IDArticulo_I?: string | null;
  Articulo_I?: string | null;
  TipoInventario_I?: string | null;
  /** Disponible (available for sale). Internal name is IngresadoI; PowerApps labels it "StockDisponible_I". */
  StockIngresado_I?: string | null;
  /** Cotizado (open cotización, pre-accept). Display name "StockCotizado_I". */
  StockPendiente_I?: string | null;
  /** Reservado Pendiente (accepted venta, pago pending). Display name "StockReservadoPendiente_I". */
  StockReservado_I?: string | null;
  StockReservadoParcial_I?: string | null;
  StockPendienteDespacho_I?: string | null;
  StockPorIngresar_I?: string | null;
  StockPrestShow_I?: string | null;
  TotalStock_I?: string | null;
  FechaActualizacion_I?: string | null;
  HoraActualizacion_I?: string | null;
  MesActualizacion_I?: string | null;
  AnioActualizacion_I?: string | null;
  MesAnioActualizacion_I?: string | null;
  Status_I?: 'Activo' | 'Inactivo' | string | null;
};

export type InventarioItem = ListItem<InventarioFields>;

export const inventarioClient = new SharePointListClient<InventarioFields>('Inventario');

const pad2 = (n: number) => String(n).padStart(2, '0');

/** List every active Inventario row. Requires `Status_I` indexed. */
export async function listActiveInventario(): Promise<InventarioItem[]> {
  return inventarioClient.listItems({
    filter: "fields/Status_I eq 'Activo'",
    top: 5000,
  });
}

/**
 * "Available for sale" stock. `StockIngresado_I` (internal name) IS the
 * Disponible bucket — no arithmetic required. PowerApps's
 * DataSources.json renames it to `StockDisponible_I` in UI formulas
 * ([DataSources.json:3000](../../../../../msapp_extracted/References/DataSources.json)).
 *
 * Historical note: earlier versions of this helper subtracted the other
 * buckets from Ingresado; that was wrong under the live schema (where
 * Ingresado is not a master pool).
 *
 * Use this for the **acceptance** check (cotización → venta), where we
 * actually have to reserve real stock right now. For the **drafting**
 * check (lines on a cotización, before acceptance), prefer
 * `calcStockCotizable` since admins are allowed to commit pre-arrival
 * units to a quote — they'll be there by the time the customer pays.
 */
export function calcStockDisponible(inv: InventarioItem | null | undefined): number {
  if (!inv) return 0;
  return parseMoney(inv.fields.StockIngresado_I) ?? 0;
}

/**
 * Stock units a salesperson can promise on a cotización: Disponible +
 * Por Ingresar. Por Ingresar is stock already paid for by the company
 * but in transit / pending receipt — by the time the customer accepts
 * and pays, it will have flipped to Disponible. Letting cotización
 * lines draw against it lets sales close pre-arrival deals without
 * waiting for the receipt to land.
 *
 * Acceptance still uses the strict `calcStockDisponible` because that
 * step physically reserves units; you can't reserve what hasn't
 * arrived.
 */
export function calcStockCotizable(inv: InventarioItem | null | undefined): number {
  if (!inv) return 0;
  const disp = parseMoney(inv.fields.StockIngresado_I) ?? 0;
  const porIng = parseMoney(inv.fields.StockPorIngresar_I) ?? 0;
  return disp + porIng;
}

/** Importación que trae unidades de un artículo, con su fecha probable (yyyy-mm-dd). */
export type FechaPorIngresar = {
  importacionId: number;
  numero: number;
  descripcion: string | null;
  fechaProbable: string | null;
  cantidadPendiente: number;
};

/**
 * Fechas estimadas del stock por ingresar, por id de artículo. Los artículos
 * cuyo por-ingresar viene de compras nacionales no aparecen (sin fecha).
 */
export async function getFechasPorIngresar(): Promise<Record<string, FechaPorIngresar[]>> {
  const res = await apiFetch<{ fechas: Record<string, FechaPorIngresar[]> }>(
    '/api/inventario/por-ingresar/fechas',
  );
  return res.fechas;
}

/**
 * Look up the active inventario row for an articulo. Used by the
 * Configuración → Artículos delete flow to surface a warning when the
 * articulo is being soft-deleted but still has live inventory state
 * (and to cascade the soft-delete if the user proceeds).
 *
 * We can't push `IDArticulo_I` into the server-side OData filter —
 * the Supabase inventory repo only registers `Status_I`,
 * `TipoInventario_I`, and `Articulo_I` in its column map, so any
 * filter on `IDArticulo_I` 500s with `unknown OData filter column`.
 * The active inventory list is small (~200 rows) so we list every
 * Activo row and find by id client-side. Cheap and avoids backend
 * coupling — if perf ever matters, register the FK in the column
 * map instead.
 *
 * Returns `null` when no active row exists.
 */
export async function findActiveInventarioByArticulo(
  articuloId: string,
): Promise<InventarioItem | null> {
  const id = articuloId.trim();
  if (!id) return null;
  const all = await listActiveInventario();
  return all.find((row) => (row.fields.IDArticulo_I ?? '').trim() === id) ?? null;
}

/**
 * Soft-delete an inventario row. Mirrors `softDeleteArticulo` —
 * flips `Status_I` to `Inactivo` so the inventory module hides it
 * but the row stays around for audit / undelete.
 */
export async function softDeleteInventario(id: string): Promise<InventarioItem> {
  return inventarioClient.updateItem(id, { Status_I: 'Inactivo' });
}

/**
 * Create an empty Inventario row for a newly-added Articulo. Every
 * bucket starts at '0' (string — matches how the legacy app stores
 * Spanish-comma text decimals).
 */
export async function createInventarioForArticulo(args: {
  articuloId: string;
  articuloNombre: string;
  tipoInventario: string;
}): Promise<InventarioItem> {
  const now = new Date();
  const fechaActualizacion = `${pad2(now.getDate())}/${pad2(now.getMonth() + 1)}/${now.getFullYear()}`;
  return inventarioClient.createItem({
    Title: '[sumar]',
    IDArticulo_I: args.articuloId,
    Articulo_I: args.articuloNombre,
    TipoInventario_I: args.tipoInventario,
    StockIngresado_I: '0',
    StockPendiente_I: '0',
    StockReservado_I: '0',
    StockPorIngresar_I: '0',
    StockPrestShow_I: '0',
    StockReservadoParcial_I: '0',
    StockPendienteDespacho_I: '0',
    TotalStock_I: '0',
    FechaActualizacion_I: fechaActualizacion,
    HoraActualizacion_I: `${pad2(now.getHours())}:${pad2(now.getMinutes())}`,
    MesActualizacion_I: pad2(now.getMonth() + 1),
    AnioActualizacion_I: String(now.getFullYear()),
    MesAnioActualizacion_I: `${pad2(now.getMonth() + 1)}/${now.getFullYear()}`,
    Status_I: 'Activo',
  });
}
